import React, { useState } from 'react'
import { Message } from 'semantic-ui-react'
import { CaretLeft } from 'phosphor-react'
import { FlexRow, FlexRowBetween, LoaderBar } from 'components'
import { AppDangerButton, AppPrimaryButton, AppSecondaryButton } from 'elements'
import { ReportApprovalService } from 'utility'
import { useNotification } from 'hooks/toast'
import { ApproveReportModal } from './ApproveReportModal'
import { RejectReportModal } from './RejectReportModal'
import { ApprovalCorrectiveTickets } from './ApprovalCorrectiveTickets'
import {
    APPROVAL_STATUS,
    extractApiError,
    formatApprovalDate,
    getApprovalConflictMessage,
    normalizeApprovalDetail,
} from './report-approval-utils'
import {
    ApprovalBanner,
    DetailLayout,
    PdfPanel,
    SidePanel,
} from './report-approval-styles'

const MetaRow = ({ label, children }) => {
    if (children === null || children === undefined || children === '') {
        return null
    }
    return (
        <div className="meta-row">
            <label>{label}</label>
            <div className="value">{children}</div>
        </div>
    )
}

export const ApprovalDetailView = ({
    report,
    loading = false,
    onBack,
    onApproved,
    // isAdmin: may approve directly (company admin, POST .../Approve). awaitingMe: the report's CURRENT approval step
    // is the caller (POST .../Advance | Reject). onChainAction: called after an Advance / Reject succeeded.
    isAdmin = true,
    awaitingMe = false,
    onChainAction,
}) => {
    const { successToast, errorToast } = useNotification()
    const [modalOpen, setModalOpen] = useState(false)
    const [chainModal, setChainModal] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')
    const [ticketsReload, setTicketsReload] = useState(0)

    const isApproved = report?.status === APPROVAL_STATUS.Approved
    const pdfUrl = isApproved
        ? report?.approvedPdfUrl || report?.pdfUrl
        : report?.pdfUrl
    const auditorDisplay =
        report?.auditorName || report?.auditorEmail || ''
    const auditOrCustomer =
        report?.customerName || report?.auditName || ''

    const handleApprove = async payload => {
        if (!report?.completedReportId) return
        try {
            setSubmitting(true)
            setSubmitError('')
            const result = await ReportApprovalService.approveReport(
                report.completedReportId,
                payload,
            )
            const updated = normalizeApprovalDetail(result)
            successToast('Report approved successfully')
            setModalOpen(false)
            ReportApprovalService.clearApprovalLookupCache()
            setTicketsReload(value => value + 1)
            onApproved?.(updated)
        } catch (error) {
            if (error?.status === 409) {
                const message = getApprovalConflictMessage(error)
                setSubmitError(message)
                errorToast(message)
            } else {
                const message = extractApiError(error)
                setSubmitError(message)
                errorToast(message)
            }
        } finally {
            setSubmitting(false)
        }
    }

    // approval workflow step (approver of the current step)
    const chainErrorMessage = error => {
        if (error?.status === 403) {
            // NOT_CURRENT_APPROVER (Message) or REJECT_NOT_ALLOWED - only the final approver may reject (ErrorMessage)
            return (
                error?.error?.Message ||
                error?.error?.ErrorMessage ||
                'This report is not waiting for your approval.'
            )
        }
        if (error?.status === 409) {
            return (
                error?.error?.ErrorMessage ||
                'This report was already approved or acted on.'
            )
        }
        return extractApiError(error)
    }

    const handleChainAction = async (kind, payload) => {
        if (!report?.completedReportId) return
        try {
            setSubmitting(true)
            setSubmitError('')
            const result =
                kind === 'reject'
                    ? await ReportApprovalService.rejectReport(
                          report.completedReportId,
                          payload,
                      )
                    : await ReportApprovalService.advanceReport(
                          report.completedReportId,
                          payload,
                      )
            if (kind === 'reject') {
                successToast('Report rejected and sent back to the auditor')
            } else if (result?.IsFullyApproved) {
                successToast('Report approved')
            } else {
                successToast('Approved - sent to the next approver')
            }
            if (result?.Warning) errorToast(result.Warning)
            setChainModal(null)
            ReportApprovalService.clearApprovalLookupCache()
            setTicketsReload(value => value + 1)
            onChainAction?.(result)
        } catch (error) {
            const message = chainErrorMessage(error)
            setSubmitError(message)
            if (!error?.roleRefusalHandled) errorToast(message)
            // someone else acted / it is no longer this user's step: refresh the buttons and history
            if (
                (error?.status === 409 ||
                    error?.error?.ErrorCode === 'NOT_CURRENT_APPROVER') &&
                !(kind === 'reject' && error?.error?.ErrorCode === 'REJECT_NOT_ALLOWED')
            ) {
                setChainModal(null)
                onChainAction?.(null)
            }
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        return <LoaderBar content="Loading report…" />
    }

    if (!report) {
        return (
            <Message negative>
                Report not found or you do not have access.
            </Message>
        )
    }

    return (
        <>
            <FlexRowBetween className="mb-3">
                <AppSecondaryButton type="button" onClick={onBack}>
                    <FlexRow className="align-items-center gap-2">
                        <CaretLeft size={18} />
                        Back to tasks
                    </FlexRow>
                </AppSecondaryButton>
                {!isApproved && awaitingMe && (
                    <FlexRow className="align-items-center gap-2">
                        <AppDangerButton
                            type="button"
                            onClick={() => setChainModal('reject')}
                        >
                            Reject
                        </AppDangerButton>
                        <AppPrimaryButton
                            type="button"
                            onClick={() => setChainModal('approve')}
                        >
                            Approve
                        </AppPrimaryButton>
                    </FlexRow>
                )}
                {!isApproved &&
                    isAdmin &&
                    awaitingMe &&
                    report.canApprove !== false && (
                        <AppSecondaryButton
                            type="button"
                            onClick={() => setModalOpen(true)}
                        >
                            Approve directly (ends the workflow)
                        </AppSecondaryButton>
                    )}
                {!isApproved &&
                    isAdmin &&
                    !awaitingMe &&
                    report.canApprove !== false && (
                        <AppPrimaryButton
                            type="button"
                            onClick={() => setModalOpen(true)}
                        >
                            Approve report
                        </AppPrimaryButton>
                    )}
            </FlexRowBetween>

            {isApproved && (
                <ApprovalBanner>
                    Approved by <strong>{report.approvedBy || 'Manager'}</strong>{' '}
                    on {formatApprovalDate(report.approvedAt)}
                    {report.approvedPdfUrl && (
                        <>
                            {' '}
                            ·{' '}
                            <a
                                href={report.approvedPdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download approved PDF
                            </a>
                        </>
                    )}
                </ApprovalBanner>
            )}

            {!isApproved &&
                report.canApprove === false &&
                report.canApproveReason && (
                    <Message warning>{report.canApproveReason}</Message>
                )}

            <ApprovalCorrectiveTickets
                auditId={report.auditId}
                reloadToken={ticketsReload}
                showApprove={
                    !isApproved && isAdmin && report.canApprove !== false
                }
                onApprove={() => setModalOpen(true)}
            />

            <DetailLayout>
                <PdfPanel>
                    {pdfUrl ? (
                        <iframe title="Report PDF preview" src={pdfUrl} />
                    ) : (
                        <Message info style={{ margin: 16 }}>
                            PDF preview is not available for this report.
                        </Message>
                    )}
                </PdfPanel>
                <SidePanel>
                    <MetaRow label="Template">
                        {report.templateName || null}
                    </MetaRow>
                    <MetaRow label="Audit">
                        {auditOrCustomer || null}
                    </MetaRow>
                    <MetaRow label="Auditor">
                        {auditorDisplay || null}
                    </MetaRow>
                    <MetaRow label="Completed">
                        {report.completedAt
                            ? formatApprovalDate(report.completedAt)
                            : null}
                    </MetaRow>
                    {report.score != null && (
                        <MetaRow label="Score">{report.score}</MetaRow>
                    )}
                    {report.questionsPassed != null &&
                        report.questionsTotal != null && (
                            <MetaRow label="Questions">
                                {report.questionsPassed} / {report.questionsTotal}
                            </MetaRow>
                        )}
                    {isApproved && report.notes && (
                        <MetaRow label="Approval notes">{report.notes}</MetaRow>
                    )}
                    {isApproved && report.signatureUrl && (
                        <MetaRow label="Signature">
                            <img
                                src={report.signatureUrl}
                                alt="Approval signature"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 80,
                                    objectFit: 'contain',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 6,
                                    background: '#fff',
                                }}
                            />
                        </MetaRow>
                    )}
                    {isApproved &&
                        !report.signatureUrl &&
                        report.signatureText && (
                            <MetaRow label="Signature">
                                {report.signatureText}
                            </MetaRow>
                        )}
                    {report.pdfUrl && (
                        <MetaRow label="Original report">
                            <a
                                href={report.pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open original PDF
                            </a>
                        </MetaRow>
                    )}
                    {isApproved && report.approvedPdfUrl && (
                        <MetaRow label="Approved report">
                            <a
                                href={report.approvedPdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Open approved PDF
                            </a>
                        </MetaRow>
                    )}
                </SidePanel>
            </DetailLayout>

            <ApproveReportModal
                open={chainModal === 'approve'}
                onClose={() => {
                    setChainModal(null)
                    setSubmitError('')
                }}
                onSubmit={payload => handleChainAction('approve', payload)}
                submitting={submitting}
                error={submitError}
            />
            <RejectReportModal
                open={chainModal === 'reject'}
                onClose={() => {
                    setChainModal(null)
                    setSubmitError('')
                }}
                onSubmit={payload => handleChainAction('reject', payload)}
                submitting={submitting}
                error={submitError}
                auditorName={auditorDisplay || 'the auditor'}
            />

            <ApproveReportModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false)
                    setSubmitError('')
                }}
                onSubmit={handleApprove}
                submitting={submitting}
                error={submitError}
            />
        </>
    )
}
